import Modal from "@via/components/modal";
import { ModalSize } from "@via/utils/constants";
import { SkeletonLoader } from "@via/components/loading/loading";
import { useMemo, useState, useEffect, type JSX } from "react";
import SelectComponent from "@via/components/select/select";
import type { SelectOption } from "@via/utils/interfaces";
import type { RunSchedulerExecution } from "common";
import {
  useSchedulerExecution,
  useSchedulerHistory,
} from "@via/pages/runs/schedulers/schedulers.api";
import { formatDateTime } from "@via/pages/account/admin/notification-settings/utils/date-helpers";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  schedulerId: string | number | null;
  schedulerName?: string;
  initialExecutionId?: number | null;
};

function formatExecutionLabel(e: RunSchedulerExecution): string {
  const started = formatDateTime(e.startedAt);
  const outcome = e.outcome ?? "unknown";
  return `${started} • ${outcome}${e.summary ? ` • ${e.summary}` : ""}`;
}

export default function SchedulerLogsModal({
  open,
  setOpen,
  schedulerId,
  schedulerName,
  initialExecutionId = null,
}: Props): JSX.Element {
  const { data: historyResp, isLoading: isHistoryLoading } =
    useSchedulerHistory(schedulerId, 20, 0, open);

  const executions = historyResp?.data ?? [];

  const executionOptions = useMemo<SelectOption[]>(() => {
    return executions.map(e => ({
      label: formatExecutionLabel(e),
      value: e.id,
    }));
  }, [executions]);

  const [selectedExecutionId, setSelectedExecutionId] = useState<number | null>(
    initialExecutionId
  );

  useEffect(() => {
    if (!open) return;
    if (initialExecutionId) {
      setSelectedExecutionId(initialExecutionId);
      return;
    }
    const first = executions[0]?.id ?? null;
    setSelectedExecutionId(first);
  }, [open, initialExecutionId, executions]);

  const { data: execution, isLoading: isExecutionLoading } =
    useSchedulerExecution(schedulerId, selectedExecutionId, open);

  const events = execution?.details?.events ?? [];

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={schedulerName ? `Logs • ${schedulerName}` : "Scheduler logs"}
      size={ModalSize.XLarge}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-900">
              Execution
            </label>
            <div className="mt-2">
              {isHistoryLoading ? (
                <SkeletonLoader count={1} />
              ) : (
                <SelectComponent
                  name="scheduler-execution-select"
                  value={
                    selectedExecutionId
                      ? (executionOptions.find(
                          o => Number(o.value) === selectedExecutionId
                        ) ?? null)
                      : null
                  }
                  onChange={selected => {
                    const next = Array.isArray(selected)
                      ? ((selected[0] as SelectOption | undefined) ?? null)
                      : (selected as SelectOption | null);
                    setSelectedExecutionId(
                      next?.value ? Number(next.value) : null
                    );
                  }}
                  options={executionOptions}
                  placeholder="Select an execution"
                  readOnly={isHistoryLoading}
                />
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900">
              Status
            </label>
            <div className="mt-2 text-sm text-gray-900">
              {execution?.finishedAt ? (
                <span className="font-medium">{execution.outcome}</span>
              ) : (
                <span className="font-medium">running</span>
              )}
            </div>
            {execution?.startedAt ? (
              <p className="mt-1 text-xs text-gray-500">
                Started {formatDateTime(execution.startedAt)}
              </p>
            ) : null}
          </div>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-3">
          <div className="mb-2 text-sm font-semibold text-gray-900">Logs</div>
          {isExecutionLoading ? (
            <SkeletonLoader count={6} />
          ) : events.length === 0 ? (
            <div className="text-sm text-gray-500">No log events yet.</div>
          ) : (
            <div className="max-h-[420px] overflow-auto space-y-2">
              {events.map((ev, idx) => (
                <div
                  key={`${ev.ts}-${idx}`}
                  className="rounded border border-gray-100 bg-gray-50 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                    <span className="font-mono">{formatDateTime(ev.ts)}</span>
                    <span className="font-semibold">{ev.level}</span>
                    <span className="text-gray-500">{ev.type}</span>
                  </div>
                  <div className="mt-1 text-sm text-gray-900">{ev.message}</div>
                  {ev.data ? (
                    <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-white p-2 text-xs text-gray-800 ring-1 ring-gray-200">
                      {JSON.stringify(ev.data, null, 2)}
                    </pre>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {execution?.summary ? (
          <p className="text-xs text-gray-600">Summary: {execution.summary}</p>
        ) : null}
      </div>
    </Modal>
  );
}
